import { create } from 'zustand'
import type { Neighborhood, Subgeo } from '@/features/subgeo/types'
import {
	type ColorPair,
	generateSingleSubgeoColor,
	generateSubgeoColors
} from '@/features/subgeo/utils/color-utils'

interface SubgeoStore {
	selectedGeo: string
	neighborhoods: Neighborhood[]
	selectedNeighborhoods: Set<string>
	subgeos: Subgeo[]
	subgeoColorMap: Map<string, ColorPair>
	editingSubgeoId: string | null
	detachedNeighborhoods: Set<string>
	highlightedNeighborhoodId: string | null
	activeTab: 'subgeos' | 'neighborhoods'
	setSelectedGeo: (geo: string) => void
	setNeighborhoods: (neighborhoods: Neighborhood[]) => void
	setSubgeos: (subgeos: Subgeo[]) => void
	toggleNeighborhood: (id: string) => void
	clearAllNeighborhoods: () => void
	clearSelectedNeighborhoods: () => void
	setNeighborhoodSubgeo: (
		neighborhoodId: string,
		subgeoId: string | null
	) => void
	moveNeighborhoodToSubgeo: (
		neighborhoodId: string,
		fromSubgeoId: string,
		toSubgeoId: string
	) => void
	refreshNeighborhoodColors: () => void
	initializeSubgeoColors: (subgeos: Subgeo[]) => void
	addSubgeoColor: (subgeoId: string) => void
	clearSubgeoColors: () => void
	setEditingSubgeoId: (id: string | null) => void
	startEditingSubgeo: (id: string) => void
	cancelEditingSubgeo: () => void
	detachNeighborhood: (id: string) => void
	restoreNeighborhood: (id: string) => void
	clearDetachedNeighborhoods: () => void
	setHighlightedNeighborhood: (id: string | null) => void
	setActiveTab: (tab: 'subgeos' | 'neighborhoods') => void
}

export const useSubgeoStore = create<SubgeoStore>((set) => ({
	selectedGeo: '',
	neighborhoods: [],
	selectedNeighborhoods: new Set(),
	subgeos: [],
	subgeoColorMap: new Map(),
	editingSubgeoId: null,
	detachedNeighborhoods: new Set(),
	highlightedNeighborhoodId: null,
	activeTab: 'subgeos',
	setSelectedGeo: (geo) =>
		set({
			selectedGeo: geo,
			neighborhoods: [],
			subgeos: [],
			selectedNeighborhoods: new Set(),
			subgeoColorMap: new Map(),
			editingSubgeoId: null,
			detachedNeighborhoods: new Set(),
			highlightedNeighborhoodId: null,
			activeTab: 'subgeos'
		}),
	setNeighborhoods: (neighborhoods) => set({ neighborhoods }),
	setSubgeos: (subgeos) => set({ subgeos }),
	toggleNeighborhood: (id) =>
		set((state) => {
			const newSelected = new Set(state.selectedNeighborhoods)
			if (newSelected.has(id)) {
				newSelected.delete(id)
			} else {
				newSelected.add(id)
			}
			return { selectedNeighborhoods: newSelected }
		}),
	clearAllNeighborhoods: () => set({ selectedNeighborhoods: new Set() }),
	clearSelectedNeighborhoods: () => set({ selectedNeighborhoods: new Set() }),

	// Future neighborhood assignment actions
	// These will be implemented when neighborhood assignment functionality is added
	setNeighborhoodSubgeo: (
		neighborhoodId: string,
		subgeoId: string | null
	) => {
		set((state) => {
			const updatedSubgeos = state.subgeos.map((subgeo) => {
				// Remove neighborhood from its current subgeo
				const filteredNeighborhoods = subgeo.neighborhoodIds.filter(
					(id) => id !== neighborhoodId
				)

				// Add neighborhood to new subgeo if specified
				if (subgeo.id === subgeoId) {
					return {
						...subgeo,
						neighborhoodIds: [
							...filteredNeighborhoods,
							neighborhoodId
						]
					}
				}

				return {
					...subgeo,
					neighborhoodIds: filteredNeighborhoods
				}
			})

			return { subgeos: updatedSubgeos }
		})
	},

	moveNeighborhoodToSubgeo: (
		neighborhoodId: string,
		fromSubgeoId: string,
		toSubgeoId: string
	) => {
		set((state) => {
			const updatedSubgeos = state.subgeos.map((subgeo) => {
				if (subgeo.id === fromSubgeoId) {
					// Remove from source subgeo
					return {
						...subgeo,
						neighborhoodIds: subgeo.neighborhoodIds.filter(
							(id) => id !== neighborhoodId
						)
					}
				}
				if (subgeo.id === toSubgeoId) {
					// Add to target subgeo (avoid duplicates)
					const hasNeighborhood =
						subgeo.neighborhoodIds.includes(neighborhoodId)
					return {
						...subgeo,
						neighborhoodIds: hasNeighborhood
							? subgeo.neighborhoodIds
							: [...subgeo.neighborhoodIds, neighborhoodId]
					}
				}
				return subgeo
			})

			return { subgeos: updatedSubgeos }
		})
	},

	refreshNeighborhoodColors: () => {
		set((state) => ({ ...state }))
	},

	initializeSubgeoColors: (subgeos: Subgeo[]) => {
		set((state) => {
			const newColorMap = new Map(state.subgeoColorMap)
			const subgeosNeedingColors = subgeos.filter(
				(s) => !newColorMap.has(s.id)
			)

			if (subgeosNeedingColors.length > 0) {
				const generatedColors =
					generateSubgeoColors(subgeosNeedingColors)
				for (const [id, color] of generatedColors) {
					newColorMap.set(id, color)
				}
			}

			const updatedSubgeos = state.subgeos.map((subgeo) => {
				const colorPair = newColorMap.get(subgeo.id)
				return colorPair ? { ...subgeo, color: colorPair.fill } : subgeo
			})

			return { subgeoColorMap: newColorMap, subgeos: updatedSubgeos }
		})
	},

	addSubgeoColor: (subgeoId: string) => {
		set((state) => {
			if (state.subgeoColorMap.has(subgeoId)) {
				return state
			}

			const newColorMap = new Map(state.subgeoColorMap)
			const newColor = generateSingleSubgeoColor(
				subgeoId,
				state.subgeoColorMap
			)
			newColorMap.set(subgeoId, newColor)

			return { subgeoColorMap: newColorMap }
		})
	},

	clearSubgeoColors: () => {
		set({ subgeoColorMap: new Map() })
	},

	setEditingSubgeoId: (id: string | null) => set({ editingSubgeoId: id }),

	startEditingSubgeo: (id: string) =>
		set({
			editingSubgeoId: id,
			selectedNeighborhoods: new Set(),
			detachedNeighborhoods: new Set(),
			activeTab: 'subgeos',
			highlightedNeighborhoodId: null
		}),

	cancelEditingSubgeo: () =>
		set({
			editingSubgeoId: null,
			selectedNeighborhoods: new Set(),
			detachedNeighborhoods: new Set()
		}),

	detachNeighborhood: (id: string) =>
		set((state) => ({
			detachedNeighborhoods: new Set(state.detachedNeighborhoods).add(id)
		})),

	restoreNeighborhood: (id: string) =>
		set((state) => {
			const updated = new Set(state.detachedNeighborhoods)
			updated.delete(id)
			return { detachedNeighborhoods: updated }
		}),

	clearDetachedNeighborhoods: () => set({ detachedNeighborhoods: new Set() }),

	setHighlightedNeighborhood: (id: string | null) =>
		set({ highlightedNeighborhoodId: id }),

	setActiveTab: (tab: 'subgeos' | 'neighborhoods') =>
		set({
			activeTab: tab,
			highlightedNeighborhoodId: tab === 'subgeos' ? null : undefined
		})
}))
